import type { ReactNode } from 'react';
import type { AuditCadence, AuditDirection } from '../../api/types.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { EM_DASH, formatNumber, formatNumberExact } from '../MetricCell/format.js';
import {
  REGION_NAMES,
  accentFor,
  color,
  font,
  fontSize,
  isUiRegion,
  radius,
} from '../../theme/tokens.js';

/**
 * The Audit page's shared pieces (spec 007, PRD-Audit §6). Everything here renders a figure or a
 * verdict the SERVICE decided — the page never recomputes one (FR-AU05).
 */

/** `2026-09-26` → `Sep 26, 2026`, read as a UTC business date. */
export function formatRunDate(iso: string | null): string {
  if (!iso) return EM_DASH;
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** A count; `—` for null, never `0` (FR-055). */
export function figure(value: number | null): string {
  return value === null ? EM_DASH : formatNumber(value);
}
export function figureExact(value: number | null): string {
  return value === null ? EM_DASH : formatNumberExact(value);
}

/** A 0–100 share as a whole percentage on the face (FR-AU09). */
export function share(value: number | null): string {
  return value === null ? EM_DASH : `${Math.round(value)}%`;
}

export const EXCLUDED_REASON: Record<string, string> = {
  no_submittals: 'No submittals in the window',
  no_candidates: 'No Nursing or Allied candidates in the book',
};
export function excludedReasonText(reason: string | null): string {
  return (reason && EXCLUDED_REASON[reason]) ?? 'Excluded by the audit service';
}

// ---- Cadence pill (blueprint `freqStyle`, line 2374) ------------------------------------------

const CADENCE: Record<AuditCadence, { bg: string; ink: string }> = {
  Daily: { bg: color.cadenceDailyBg, ink: color.cadenceDailyInk },
  Weekly: { bg: color.cadenceWeeklyBg, ink: color.cadenceWeeklyInk },
  Monthly: { bg: color.cadenceMonthlyBg, ink: color.cadenceMonthlyInk },
};

/**
 * `null` (an uncatalogued audit) renders an empty slot of the same width, so the names in a
 * section still line up — it makes no cadence claim it cannot back (FR-AU42).
 */
export function CadencePill({ cadence, note }: { cadence: AuditCadence | null; note?: string | null }) {
  const base = {
    flex: 'none',
    width: 60,
    textAlign: 'center' as const,
    padding: '4px 0',
    borderRadius: radius.pill,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '.3px',
  };
  if (!cadence) return <span aria-hidden="true" style={base} />;
  const style = CADENCE[cadence];
  return (
    <span title={note ?? `${cadence} audit`} style={{ ...base, background: style.bg, color: style.ink }}>
      {cadence}
    </span>
  );
}

// ---- Status pill ------------------------------------------------------------------------------

export function StatusPill({
  status,
  reason,
}: {
  status: 'flagged' | 'pass' | 'excluded' | null;
  reason?: string | null;
}) {
  if (status === null) return <span aria-label="No status">{''}</span>;
  const styles = {
    flagged: { label: 'Flagged', bg: color.dangerBg, ink: color.negative },
    pass: { label: 'Pass', bg: color.washMint, ink: color.inkMintStrong },
    excluded: { label: 'Excluded', bg: color.surfaceSubtle, ink: color.inkMuted },
  }[status];
  return (
    <span
      title={status === 'excluded' ? excludedReasonText(reason ?? null) : undefined}
      style={{
        fontSize: '11px',
        fontWeight: 600,
        padding: '2px 9px',
        borderRadius: radius.pill,
        background: styles.bg,
        color: styles.ink,
        whiteSpace: 'nowrap',
      }}
    >
      {styles.label}
    </span>
  );
}

// ---- Region pill ------------------------------------------------------------------------------

export function regionName(code: string): string {
  return isUiRegion(code) ? REGION_NAMES[code] : code;
}

/**
 * The ROSTER's region, with the full name on hover — and, where the audit service evaluated the
 * recruiter against a different region or none, the hover says so (FR-AU27). Measured 2026-09-27:
 * 15 in-scope recruiters have no service region and 3 SPOT recruiters are "Region 4" there.
 */
export function RegionPill({ region, serviceRegion }: { region: string; serviceRegion: string | null }) {
  const name = regionName(region);
  const differs = serviceRegion === null || !serviceRegionMatches(region, serviceRegion);
  const note = serviceRegion === null
    ? 'The audit service has no region for this recruiter.'
    : differs
      ? `The audit service evaluated this recruiter against ${serviceRegion}.`
      : null;
  return (
    <Disclosure
      accessibleName={`${region}, ${name}${note ? `. ${note}` : ''}`}
      panel={
        <span>
          <strong>{region}</strong>
          <span style={{ display: 'block' }}>{name}</span>
          {note ? <span style={{ display: 'block', marginTop: 4 }}>{note}</span> : null}
        </span>
      }
    >
      <span
        className="mono"
        style={{
          fontFamily: font.mono,
          fontSize: '10px',
          fontWeight: 600,
          color: color.onAccent,
          background: accentFor(region),
          padding: '2px 6px',
          borderRadius: radius.sm,
        }}
      >
        {region}
      </span>
    </Disclosure>
  );
}

/** `Region 3 Nursing` agrees with `R3`. SPOT/SPA have no numbered counterpart there. */
function serviceRegionMatches(region: string, serviceRegion: string): boolean {
  const m = /^Region (\d)\b/.exec(serviceRegion);
  return m !== null && region === `R${m[1]}`;
}

// ---- Trends (FR-AU22–24, AU-15) -----------------------------------------------------------------

export type Tone = 'good' | 'bad' | 'neutral';

export interface Trend {
  glyph: '▲' | '▼' | '—' | null;
  tone: Tone;
  tooltip: string;
}

/**
 * ⚠ Coloured by MEANING, not direction — on this page only (constitution 3.0.0, Principle I
 * exception). The arrow still points the way the number moved; it is red when that is a
 * worsening and green when it is an improvement, per the audit's declared direction.
 */
export function toneFor(delta: number, direction: AuditDirection): Tone {
  if (delta === 0 || direction === 'neutral') return 'neutral';
  const rose = delta > 0;
  if (direction === 'worse') return rose ? 'bad' : 'good';
  return rose ? 'good' : 'bad';
}

export function toneColor(tone: Tone): string {
  return tone === 'bad' ? color.negativeStrong : tone === 'good' ? color.positive : color.trendNeutral;
}

/** A cell's comparison with the same recruiter on the audit's previous run. */
export function cellTrend(args: {
  current: number | null;
  previous: number | null | undefined;
  direction: AuditDirection;
  previousRunDate: string | null;
  /** The recruiter had a row on the previous run. */
  hadPreviousRow: boolean;
  excludedNowOrThen: boolean;
  render: (value: number) => string;
}): Trend {
  const { current, previous, direction, previousRunDate, hadPreviousRow, excludedNowOrThen, render } = args;
  if (!previousRunDate) return { glyph: null, tone: 'neutral', tooltip: 'First run of this audit' };
  const when = formatRunDate(previousRunDate);
  if (!hadPreviousRow) {
    return { glyph: null, tone: 'neutral', tooltip: `Not checked on the previous run (${when})` };
  }
  if (excludedNowOrThen) {
    return { glyph: null, tone: 'neutral', tooltip: `Excluded on this or the previous run (${when})` };
  }
  if (current === null || previous === null || previous === undefined) {
    return { glyph: null, tone: 'neutral', tooltip: `No comparable figure on the previous run (${when})` };
  }
  const delta = current - previous;
  if (delta === 0) return { glyph: '—', tone: 'neutral', tooltip: `Same as the previous run (${when})` };
  return {
    glyph: delta > 0 ? '▲' : '▼',
    tone: toneFor(delta, direction),
    tooltip: `Previous run (${when}): ${render(previous)}`,
  };
}

/** The run history's headline column against the prior run. */
export function runTrend(
  delta: number | null,
  direction: AuditDirection,
  previousRunDate: string | null,
  noun: string,
): Trend {
  if (delta === null || !previousRunDate) return { glyph: null, tone: 'neutral', tooltip: 'No prior run to compare' };
  const when = formatRunDate(previousRunDate);
  if (delta === 0) return { glyph: '—', tone: 'neutral', tooltip: `Same as the prior run (${when})` };
  return {
    glyph: delta > 0 ? '▲' : '▼',
    tone: toneFor(delta, direction),
    tooltip: `${formatNumberExact(Math.abs(delta))} ${delta > 0 ? 'more' : 'fewer'} ${noun} than the prior run (${when})`,
  };
}

export function TrendGlyph({ trend }: { trend: Trend }) {
  // `aria-hidden`: the glyph summarises the tooltip, which reaches assistive technology through
  // the cell's accessible name.
  return (
    <span
      aria-hidden="true"
      style={{
        flex: '0 0 10px',
        fontSize: fontSize.micro,
        fontWeight: 700,
        color: toneColor(trend.tone),
        textAlign: 'left',
      }}
    >
      {trend.glyph ?? ''}
    </span>
  );
}

/**
 * A table cell's content box. `<DataTable>` aligns HEADERS by `column.align` but leaves each
 * renderer to place its own content (as `<MetricCell>` does), so the Audit tables align theirs
 * here — with the blueprint's 11px row padding and 13px text (lines 1082–1095).
 */
export function Cell({ align = 'left', children }: { align?: 'left' | 'right'; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        gap: 5,
        padding: '11px 4px',
        fontSize: fontSize.control,
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}
